import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DomPurify from 'dompurify';
import Yup from 'utils/validation/yup-extensions';
import { UserContext } from 'context/User/UserContext';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import PageWithBreadcrumbs from '../../EpicAdministration/layouts/PageWithBreadcrumbs';
import Confirm from 'components/AppCart/GenericApps/Confirm/Confirm';
import { HomeButton } from 'components/UI/Input/Button';
import { getSite, postAssociation } from '../_api';
import { getExistingEmail } from 'components/Form/TextBox/variants/EmailTextBox/_api';
import { clearMessages } from 'store/ancillary/actions';
import { raiseErrorBanner } from 'components/MessageDispatch/messageDispatch';
import { isValidEmail } from 'utils';
import { SitePayload, SponsorPayload } from '../_classes';

/**
 * EpicCare Link Site Sponsor Form for manually assigning a sponsor to a requested site that failed to automate.
 * @param {any} props properties from router
 * @returns EpicCare Link Site Sponsor Association page view
 */
const EditorView = props => {
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const dispatch = useDispatch();

	const query = new URLSearchParams(props.location.search);
	const id = query.get('id');

	const [sErrorMessage, setErrorMessage] = useState('');
	const [sLoading, setLoading] = useState(false);
	const [sSiteName, setSiteName] = useState('');
	const [sSiteAdminName, setSiteAdminName] = useState('');
	const [sSponsorEmail, setSponsorEmail] = useState('');
	const [sSuccess, setSuccess] = useState(false);

	useEffect(() => {
		let ignore = false;

		const render = async (id = null) => {
			dispatch(clearMessages());

			if (id === null) {
				raiseErrorBanner('No site ID provided.', dispatch);
				setLoading(false);
				return;
			}

			if (!Yup.number().integer().isValidSync(id)) {
				raiseErrorBanner('Unexpected ID provided.', dispatch);
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				const siteRequest = await getSite(id, dispatch);
				if (!ignore && siteRequest.msgID > -1) {
					setSiteName(siteRequest.siteName);
					setSiteAdminName(siteRequest.siteAdminName);
				}
			} catch (ex) {
				raiseErrorBanner(ex, dispatch);
			} finally {
				setLoading(false);
			}
		};

		render(id);

		return () => {
			ignore = true;
		};
	}, [dispatch, id]);

	const validate = useCallback(
		async email => {
			let arrEmails = [];
			let message = '';
			let valid = false;

			try {
				arrEmails = email
					.trim()
					.split(';')
					.map(e => e.trim());
				valid = arrEmails.every(el => isValidEmail(el));
				message = valid ? '' : 'Invalid email address(es).';

				if (valid) {
					const results = await Promise.all(
						arrEmails.map(el => getExistingEmail(el))
					);
					valid = results?.every(result => result?.length > 0);
					message = valid ? '' : 'User email(s) not found.';
				}
			} catch (ex) {
				raiseErrorBanner(ex, dispatch);
			} finally {
				setErrorMessage(message);
				return valid;
			}
		},
		[dispatch]
	);

	const onSubmitHandler = useCallback(async () => {
		setLoading(true);
		try {
			const emailSanitized = DomPurify.sanitize(sSponsorEmail?.trim());

			if (await validate(emailSanitized)) {
				const response = await postAssociation(
					new SponsorPayload(
						contextUser?.userInfo?.impersonationProfile
							? contextUser.userInfo.impersonationProfile.ID
							: contextUser.userInfo.userProfile.ID,
						emailSanitized,
						new SitePayload(id, sSiteName, sSiteAdminName)
					),
					dispatch
				);
				setSuccess(response);
			}
		} catch (ex) {
			raiseErrorBanner(ex, dispatch);
		} finally {
			setLoading(false);
		}
	}, [
		dispatch,
		validate,
		contextUser,
		id,
		sSiteName,
		sSiteAdminName,
		sSponsorEmail,
	]);

	return (
		<PageWithBreadcrumbs
			currentPageLabel={'Site Sponsor Association'}
			previousPageLabel='EpicCare Link Administration Home'
			previousPageLink='/Utilities/EpicCareLink/Home'
		>
			<WithLoadingHandler showChildren={!sLoading} isLoading={sLoading}>
				{!sSuccess && sSiteName !== '' && sSiteAdminName !== '' && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							rowGap: '16px',
						}}
					>
						<TextField
							id='text-field--site-name'
							label='Site Name'
							disabled
							value={sSiteName}
						/>
						<TextField
							id='text-field--site-admin-name'
							label='Site Admin Name'
							disabled
							value={sSiteAdminName}
						/>
						<TextField
							id='text-field--sponsor-email'
							error={sErrorMessage !== ''}
							helperText={
								sErrorMessage !== ''
									? sErrorMessage
									: 'Separate with semicolon.'
							}
							inputProps={{ maxLength: 255 }}
							label='Sponsor UPMC Email(s)'
							onBlur={e => validate(e.target.value)}
							onChange={e => {
								if (sErrorMessage !== '') {
									setErrorMessage('');
								}
								setSponsorEmail(e.target.value);
							}}
						/>
						<Button
							id='button--submit'
							variant='contained'
							onClick={onSubmitHandler}
						>
							Submit
						</Button>
					</Box>
				)}
				{sSuccess && (
					<Confirm
						actionButtons={<HomeButton icon />}
						bodyMessage={
							<span>
								EpicCare Link site <b>{sSiteName}</b> has been
								successfully associated with provided UPMC
								sponsor <b>{sSponsorEmail}</b>. This site's
								request will now continue to automate.
							</span>
						}
					/>
				)}
			</WithLoadingHandler>
		</PageWithBreadcrumbs>
	);
};

export default EditorView;
